export interface BlogContentData {
  title: string;
  description: string;
  content: string;
  keywords: string[];
  category: string;
  author: string;
  date: string;
  readTime: string;
  relatedServices: string[];
}

export const keywordToContentMap: Record<string, Partial<BlogContentData>> = {
  ashburn: {
    title: 'Ashburn Data Center Solutions',
    description: 'Discover premium colocation and cloud services in Ashburn, Virginia - the heart of internet infrastructure.',
    category: 'Data Center',
    keywords: ['ashburn', 'data center', 'colocation', 'virginia'],
    relatedServices: ['Baremetal Servers', 'VPS Servers', 'Colocation'],
  },
  colocation: {
    title: 'Enterprise Colocation Services',
    description: 'Secure, scalable colocation solutions for businesses of all sizes with 24/7 support and premium connectivity.',
    category: 'Infrastructure',
    keywords: ['colocation', 'data center', 'enterprise', 'hosting'],
    relatedServices: ['Baremetal Servers', 'Data Center'],
  },
  proxy: {
    title: 'High-Performance Proxy Solutions',
    description: 'Professional proxy services for businesses, including residential and datacenter proxies with global coverage.',
    category: 'Networking',
    keywords: ['proxy', 'residential proxies', 'datacenter proxies', 'privacy'],
    relatedServices: ['Residential Proxies'],
  },
  proxies: {
    title: 'Advanced Proxy Network Services',
    description: 'Comprehensive proxy solutions with premium IPs, high speeds, and reliable connectivity for your business needs.',
    category: 'Networking',
    keywords: ['proxies', 'proxy network', 'ip addresses', 'connectivity'],
    relatedServices: ['Residential Proxies'],
  },
  vps: {
    title: 'Virtual Private Server Solutions',
    description: 'Powerful VPS hosting with full root access, SSD storage, and guaranteed resources for your applications.',
    category: 'Cloud Computing',
    keywords: ['vps', 'virtual private server', 'cloud hosting', 'ssd'],
    relatedServices: ['VPS Servers', 'Baremetal Servers'],
  },
  residential: {
    title: 'Residential Proxy & RDP Services',
    description: 'Premium residential IP solutions including proxies and remote desktop access with global coverage.',
    category: 'Networking',
    keywords: ['residential', 'proxy', 'rdp', 'remote desktop'],
    relatedServices: ['Residential Proxies', 'Residential Remote Desktops'],
  },
  datacenter: {
    title: 'Enterprise Data Center Services',
    description: 'State-of-the-art data center facilities with redundant power, cooling, and connectivity infrastructure.',
    category: 'Infrastructure',
    keywords: ['data center', 'infrastructure', 'enterprise', 'redundancy'],
    relatedServices: ['Baremetal Servers', 'VPS Servers'],
  },
  'data-center': {
    title: 'Data Center Infrastructure Solutions',
    description: 'Comprehensive data center services including colocation, managed hosting, and cloud infrastructure.',
    category: 'Infrastructure',
    keywords: ['data center', 'infrastructure', 'managed hosting', 'cloud'],
    relatedServices: ['Baremetal Servers', 'VPS Servers'],
  },
  mobile: {
    title: 'Mobile Connectivity Solutions',
    description: 'Advanced mobile proxy and connectivity services for businesses requiring mobile IP addresses.',
    category: 'Mobile',
    keywords: ['mobile', 'mobile proxies', 'connectivity', 'mobile ips'],
    relatedServices: ['Mobile Solutions'],
  },
  brokerage: {
    title: 'Infrastructure Brokerage Services',
    description: 'Expert infrastructure brokerage and consulting services to optimize your technology investments.',
    category: 'Consulting',
    keywords: ['brokerage', 'consulting', 'infrastructure', 'optimization'],
    relatedServices: ['Consultation'],
  },
};

export const generateDynamicContent = (slug: string): BlogContentData => {
  const keywords = extractKeywords(slug);
  const primaryKeyword = keywords[0] || 'infrastructure';
  
  const baseContent = keywordToContentMap[primaryKeyword] || {
    title: 'NEXX Infrastructure Solutions',
    description: 'Comprehensive infrastructure solutions for modern businesses with premium support and reliability.',
    category: 'Infrastructure',
    keywords: ['infrastructure', 'solutions', 'business'],
    relatedServices: ['Consultation'],
  };

  const content = generateContentBody(keywords, baseContent);

  return {
    title: baseContent.title || 'NEXX Infrastructure Solutions',
    description: baseContent.description || 'Professional infrastructure solutions for your business needs.',
    content,
    keywords: baseContent.keywords || keywords,
    category: baseContent.category || 'Infrastructure',
    author: 'NEXX Team',
    date: new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }),
    readTime: '5 min read',
    relatedServices: baseContent.relatedServices || ['Consultation'],
  };
};

const extractKeywords = (slug: string): string[] => {
  const words = slug
    .toLowerCase()
    .split(/[-_\s]+/)
    .filter(word => word.length > 2)
    .filter(word => !['the', 'and', 'for', 'with', 'your', 'how', 'why', 'what'].includes(word));

  const priorityKeywords = Object.keys(keywordToContentMap);
  const foundKeywords = words.filter(word => priorityKeywords.includes(word));
  
  return foundKeywords.length > 0 ? foundKeywords : words.slice(0, 3);
};

const generateContentBody = (keywords: string[], baseContent: Partial<BlogContentData>): string => {
  const primaryKeyword = keywords[0] || 'infrastructure';
  const category = baseContent.category || 'Infrastructure';
  
  return `
    <div class="prose prose-lg max-w-none">
      <p class="text-xl text-muted-foreground mb-8">
        In today's digital landscape, businesses require reliable and scalable ${primaryKeyword} solutions 
        to stay competitive and meet growing demands.
      </p>

      <h2 class="text-2xl font-bold mb-4 text-foreground">Why Choose NEXX for ${category}?</h2>
      
      <p class="mb-6">
        At NEXX, we understand that every business has unique requirements. Our ${primaryKeyword} solutions 
        are designed to provide maximum performance, reliability, and scalability while maintaining 
        cost-effectiveness.
      </p>

      <h3 class="text-xl font-semibold mb-3 text-foreground">Key Benefits</h3>
      <ul class="list-disc pl-6 mb-6 space-y-2">
        <li><strong>24/7 Expert Support:</strong> Our team of infrastructure specialists is available around the clock</li>
        <li><strong>Enterprise-Grade Security:</strong> Advanced security measures to protect your data and applications</li>
        <li><strong>Scalable Solutions:</strong> Grow your infrastructure as your business expands</li>
        <li><strong>Competitive Pricing:</strong> Cost-effective solutions without compromising on quality</li>
        <li><strong>Global Reach:</strong> Multiple data center locations for optimal performance</li>
      </ul>

      <h3 class="text-xl font-semibold mb-3 text-foreground">Our Approach</h3>
      <p class="mb-6">
        We believe in providing tailored solutions that align with your specific business objectives. 
        Our experienced team works closely with you to understand your requirements and design 
        infrastructure that delivers optimal performance and value.
      </p>

      <div class="bg-muted p-6 rounded-lg mb-6">
        <h4 class="font-semibold mb-3 text-foreground">Ready to Get Started?</h4>
        <p class="text-muted-foreground">
          Contact our team today to discuss your ${primaryKeyword} requirements and learn how 
          NEXX can help optimize your infrastructure strategy.
        </p>
      </div>

      <h3 class="text-xl font-semibold mb-3 text-foreground">Industry Expertise</h3>
      <p class="mb-6">
        With years of experience in the infrastructure industry, NEXX has built a reputation 
        for delivering reliable, high-performance solutions across various sectors including 
        finance, healthcare, e-commerce, and technology.
      </p>

      <p class="text-lg font-medium text-center py-8">
        Trust NEXX for your ${category.toLowerCase()} needs and experience the difference 
        that professional infrastructure solutions can make for your business.
      </p>
    </div>
  `;
};